import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { CreateChatFormValues } from "@/features/chat/schema";

interface ChatFormProps {
  form: UseFormReturn<CreateChatFormValues>;
  titleMaxLength?: number;
  messageMaxLength?: number;
  disabled?: boolean;
}

export const ChatForm: React.FC<ChatFormProps> = ({
  form,
  titleMaxLength = 30,
  messageMaxLength = 500,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const titleLength = form.watch("title")?.length ?? 0;
  const messageLength = form.watch("message")?.length ?? 0;

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{t("common.title")} *</FormLabel>
              <span className="text-muted-foreground text-xs">
                {titleLength}/{titleMaxLength}
              </span>
            </div>
            <FormControl>
              <Input
                {...field}
                placeholder={t("chat.titlePlaceholder")}
                maxLength={titleMaxLength}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{t("chat.firstMessage")} *</FormLabel>
              <span className="text-muted-foreground text-xs">
                {messageLength}/{messageMaxLength}
              </span>
            </div>
            <FormControl>
              <Textarea
                {...field}
                placeholder={t("chat.messagePlaceholder")}
                className="min-h-32"
                maxLength={messageMaxLength}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
